"use client";

import React, { useState, useMemo, useEffect } from "react";
import PerfectMarkdownRenderer from "../../common/PerfectMarkdownRenderer";
import { Section } from "../../../types/documentTypes";
import YooptaMarkdownEditor from "../../editor/YooptaMarkdownEditor";
import { renderMarkdownToHTML } from "../../../utils/documentUtils";
import { Button } from "../../../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../../../foundation/altzero_base/base/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../foundation/altzero_base/base/components/ui/dialog";
import {
  Eye,
  Edit3,
  FileText,
  Wand2,
  Loader2,
  Save,
  Trash2,
  Plus,
} from "lucide-react";

// Types
interface YooptaContentEditorProps {
  sections: Section[];
  readonly?: boolean;
  onSectionUpdate?: (sectionId: string, updatedSection: Section) => void;
  onPreviewUpdate?: (sectionId: string, updatedSection: Section) => void;
  onSectionRegenerate?: (sectionId: string, prompt: string) => void;
  onSectionTitleChange?: (sectionId: string, newTitle: string) => void;
  onSectionDelete?: (sectionId: string) => void;
  onSectionAdd?: () => void;
  onSectionAddAfter?: (afterSectionId: string) => void;
  userId?: string;
  className?: string;
  initialSelectedSectionId?: string; // 🚨 NEW: Preserve selected section
  onSectionSelect?: (sectionId: string) => void; // 🚨 NEW: Track section selection
  hideSectionList?: boolean; // 🚨 NEW: Hide the internal section list
}

// Main YooptaContentEditor Component
const YooptaContentEditor: React.FC<YooptaContentEditorProps> = ({
  sections,
  readonly = false,
  onSectionUpdate,
  onPreviewUpdate,
  onSectionRegenerate,
  onSectionTitleChange,
  onSectionDelete,
  onSectionAdd,
  onSectionAddAfter,
  userId,
  className = "",
  initialSelectedSectionId, // 🚨 NEW: Preserve selected section
  onSectionSelect, // 🚨 NEW: Track section selection
  hideSectionList = false, // 🚨 NEW: Hide the internal section list
}) => {
  // 🚨 FIXED: Safe section selection - prevent "all sections selected" bug
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(() => {
    console.log('🔄 YooptaContentEditor: Initial state setup', {
      initialSelectedSectionId,
      sectionsCount: sections.length,
      sectionIds: sections.map(s => s.id),
      sectionTitles: sections.map(s => s.title)
    });

    // CRITICAL: Always start with null to prevent race conditions
    // Let useEffect handle the selection after component is fully mounted
    return null;
  });
  // 🚨 FIXED: Per-section editing state instead of global
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
  // 🚨 REMOVED: Internal regeneration dialog state - parent component handles this now
  const [previewUpdateTrigger, setPreviewUpdateTrigger] = useState(0); // Force preview re-render
  const [editorKey, setEditorKey] = useState(0); // Force editor re-initialization when needed
  const [editedContent, setEditedContent] = useState<{[sectionId: string]: string}>({}); // Track edited content
  const [editingTitles, setEditingTitles] = useState<Set<string>>(new Set());
  const [titleInputs, setTitleInputs] = useState<{[sectionId: string]: string}>({});
  const [isSaving, setIsSaving] = useState(false);

  // 🚨 FIXED: Update selectedSectionId when initialSelectedSectionId changes (AI Generation safe)
  useEffect(() => {
    // Only update if initialSelectedSectionId is provided and different from current
    if (initialSelectedSectionId &&
        initialSelectedSectionId !== selectedSectionId &&
        sections.some(s => s.id === initialSelectedSectionId)) {

      console.log('🔄 YooptaContentEditor: Updating selected section from prop:', {
        from: selectedSectionId,
        to: initialSelectedSectionId,
        sectionsCount: sections.length
      });

      setSelectedSectionId(initialSelectedSectionId);

      // Notify parent about the selection change
      if (onSectionSelect) {
        onSectionSelect(initialSelectedSectionId);
      }
    }
  }, [initialSelectedSectionId, sections.length]); // 🚨 FIXED: Only depend on sections.length, not full sections array

  // 🚨 FIXED: Bulletproof section selection - prevent all bugs
  useEffect(() => {
    console.log('🔄 YooptaContentEditor: Section validation useEffect triggered', {
      sectionsCount: sections.length,
      selectedSectionId,
      initialSelectedSectionId,
      sectionIds: sections.map(s => s.id),
      sectionTitles: sections.map(s => s.title)
    });

    // Only run if we have sections
    if (!sections || sections.length === 0) {
      if (selectedSectionId) {
        console.log('🔄 No sections available, clearing selection');
        setSelectedSectionId(null);
      }
      return;
    }

    // CRITICAL: Validate section IDs are unique
    const sectionIds = sections.map(s => s.id);
    const uniqueIds = new Set(sectionIds);
    if (sectionIds.length !== uniqueIds.size) {
      console.error('🚨 CRITICAL BUG: Duplicate section IDs detected!', {
        totalSections: sectionIds.length,
        uniqueIds: uniqueIds.size,
        duplicateIds: sectionIds.filter((id, index) => sectionIds.indexOf(id) !== index),
        allIds: sectionIds
      });
    }

    // Case 1: Selected section no longer exists - find replacement
    if (selectedSectionId && !sections.some(s => s.id === selectedSectionId)) {
      console.log('🔄 Selected section no longer exists, selecting first:', {
        oldSelection: selectedSectionId,
        newSelection: sections[0].id,
        totalSections: sections.length
      });
      setSelectedSectionId(sections[0].id);
      if (onSectionSelect) {
        onSectionSelect(sections[0].id);
      }
      return;
    }

    // Case 2: No section selected - auto-select first section ONCE
    if (!selectedSectionId && sections.length > 0) {
      console.log('🔄 No selection, auto-selecting first section:', {
        firstSectionId: sections[0].id,
        firstSectionTitle: sections[0].title
      });
      setSelectedSectionId(sections[0].id);
      if (onSectionSelect) {
        onSectionSelect(sections[0].id);
      }
    }
  }, [sections.length, selectedSectionId, onSectionSelect]); // Stable dependencies

  // Get selected section
  const selectedSection = useMemo(() => {
    console.log('🔍 selectedSection calculation:', {
      selectedSectionId,
      sectionsCount: sections.length,
      sectionIds: sections.map(s => s.id),
      foundSection: sections.find((s) => s.id === selectedSectionId)?.title || 'NOT FOUND'
    });
    return sections.find((s) => s.id === selectedSectionId) || null;
  }, [sections, selectedSectionId]);

  // Handle section content change from Yoopta editor
  const handleSectionContentChange = (markdown: string) => {
    if (!selectedSection) return;

    console.log('🚨 YooptaContentEditor handleSectionContentChange:', {
      sectionId: selectedSection.id,
      contentLength: markdown?.length || 0,
      hasContent: !!(markdown && markdown.trim()),
      preview: markdown ? markdown.substring(0, 50) + '...' : 'NO CONTENT'
    });

    // Track edited content
    setEditedContent(prev => ({
      ...prev,
      [selectedSection.id]: markdown
    }));

    const updatedSection = {
      ...selectedSection,
      content: markdown,
      edited: true,
      lastModified: new Date().toISOString(),
    };
    
    if (onPreviewUpdate) {
      onPreviewUpdate(selectedSection.id, updatedSection);
    }

    // Force preview update
    setPreviewUpdateTrigger(prev => prev + 1);
  };

  // Handle title editing
  const toggleTitleEditing = (sectionId: string) => {
    console.log('🔄 toggleTitleEditing called:', {
      sectionId,
      currentlyEditing: editingTitles.has(sectionId),
      sectionTitle: sections.find(s => s.id === sectionId)?.title
    });

    const newEditing = new Set(editingTitles);
    if (newEditing.has(sectionId)) {
      newEditing.delete(sectionId);
      // Save the title when exiting edit mode
      const newTitle = titleInputs[sectionId];
      if (newTitle && newTitle.trim() && onSectionTitleChange) {
        console.log('🔄 Saving title change:', { sectionId, newTitle });
        onSectionTitleChange(sectionId, newTitle.trim());
      }
    } else {
      newEditing.add(sectionId);
      // CRITICAL: Initialize the input with current section's title
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        console.log('🔄 Initializing title input:', { sectionId, title: section.title });
        setTitleInputs(prev => ({
          ...prev,
          [sectionId]: section.title
        }));
      }
    }
    setEditingTitles(newEditing);
  };

  const handleTitleChange = (sectionId: string, newTitle: string) => {
    setTitleInputs(prev => ({
      ...prev,
      [sectionId]: newTitle
    }));
  };

  const handleTitleSave = (sectionId: string) => {
    const newTitle = titleInputs[sectionId];
    if (newTitle && newTitle.trim() && onSectionTitleChange) {
      onSectionTitleChange(sectionId, newTitle.trim());
    }
    setEditingTitles(prev => {
      const newEditing = new Set(prev);
      newEditing.delete(sectionId);
      return newEditing;
    });
  };

  const handleTitleCancel = (sectionId: string) => {
    setEditingTitles(prev => {
      const newEditing = new Set(prev);
      newEditing.delete(sectionId);
      return newEditing;
    });
    // Reset the input to original title
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      setTitleInputs(prev => ({
        ...prev,
        [sectionId]: section.title
      }));
    }
  };

  // Handle save functionality
  const handleSave = async () => {
    const selectedSection = sections.find(s => s.id === selectedSectionId);
    if (selectedSection && onSectionUpdate) {
      setIsSaving(true);
      try {
        // Get the edited content for this section
        const currentContent = editedContent[selectedSection.id] || selectedSection.content;

        // Only save if there are actual changes
        if (currentContent === selectedSection.content) {
          console.log('🚨 No changes to save for section:', selectedSection.id);
          setIsSaving(false);
          return;
        }

        const updatedSection = {
          ...selectedSection,
          content: currentContent,
          edited: true,
          lastModified: new Date().toISOString(),
        };

        console.log('🚨 YooptaContentEditor handleSave - SAVING TO DATABASE:', {
          sectionId: selectedSection.id,
          originalContentLength: selectedSection.content?.length || 0,
          editedContentLength: currentContent?.length || 0,
          hasChanges: currentContent !== selectedSection.content,
          preview: currentContent ? currentContent.substring(0, 50) + '...' : 'NO CONTENT'
        });

        // Save to database
        await onSectionUpdate(selectedSection.id, updatedSection);

        // Clear edited content after successful save
        setEditedContent(prev => {
          const newEditedContent = { ...prev };
          delete newEditedContent[selectedSection.id];
          return newEditedContent;
        });

        console.log('✅ Section saved successfully to database');

      } catch (error) {
        console.error('❌ Save failed:', error);
        // Show error toast or notification here if needed
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error('❌ Cannot save: missing selectedSection or onSectionUpdate callback');
    }
  };

  const handleDeleteSection = (sectionId: string) => {
    if (sections.length <= 1) {
      // Show error toast or alert
      return;
    }
    
    if (onSectionDelete) {
      onSectionDelete(sectionId);
    }
    
    // If this was the selected section, select the first available section
    if (selectedSectionId === sectionId) {
      const remainingSections = sections.filter(s => s.id !== sectionId);
      if (remainingSections.length > 0) {
        setSelectedSectionId(remainingSections[0].id);
      } else {
        setSelectedSectionId(null);
      }
    }
  };



  // 🚨 REMOVED: Internal regeneration handler - parent component handles this now



  // 🚨 DEBUG: Log component render state
  console.log('🔍 YooptaContentEditor RENDER:', {
    sectionsCount: sections?.length || 0,
    selectedSectionId,
    initialSelectedSectionId,
    sectionIds: sections?.map(s => s.id) || [],
    sectionTitles: sections?.map(s => s.title) || []
  });

  if (!sections || sections.length === 0) {
    console.log('🔍 YooptaContentEditor: No sections, showing empty state');
    return (
      <div className={`yoopta-content-editor ${className}`}>
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg font-medium mb-2">No sections available</div>
          <div className="text-sm">
            Create sections to start editing content
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`yoopta-content-editor ${className}`}>
      {/* Two-Column Layout */}
      <div className="flex flex-1 overflow-hidden bg-gray-50 rounded-lg border">
        {/* Column 1: Section List - Only show if not hidden */}
        {!hideSectionList && (
          <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-shrink-0 p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Document Sections
            </h3>
            <p className="text-sm text-gray-600">
              {sections.length} sections
            </p>
          </div>

          <div className="flex-1">
            <div className="p-2">
              {sections.map((section) => {
                const isSelected = selectedSectionId === section.id;
                console.log('🔍 Section render check:', {
                  sectionId: section.id,
                  selectedSectionId,
                  isSelected,
                  sectionTitle: section.title
                });

                return (
                <div
                  key={section.id}
                  className={`p-3 mb-2 border rounded-lg transition-all cursor-pointer ${
                    isSelected
                      ? "bg-blue-50 border-blue-300 shadow-sm"
                      : "bg-white hover:border-blue-200 hover:shadow-sm"
                  }`}
                  onClick={() => {
                    setSelectedSectionId(section.id);
                    // 🚨 NEW: Notify parent about section selection
                    if (onSectionSelect) {
                      onSectionSelect(section.id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm text-gray-900 line-clamp-2">
                      {editingTitles.has(section.id) ? (
                        <Input
                          value={titleInputs[section.id] !== undefined ? titleInputs[section.id] : section.title || ''}
                          onChange={(e) => {
                            console.log('🔄 Title input changed:', { sectionId: section.id, newValue: e.target.value });
                            handleTitleChange(section.id, e.target.value);
                          }}
                          onBlur={() => handleTitleSave(section.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleTitleSave(section.id);
                            } else if (e.key === 'Escape') {
                              handleTitleCancel(section.id);
                            }
                          }}
                          className="w-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      ) : (
                        section.title
                      )}
                    </h4>
                    <div className="flex items-center gap-1 ml-2">
                      {section.edited && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {section.content
                      ? section.content.substring(0, 100) +
                        (section.content.length > 100 ? "..." : "")
                      : "No content"}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      {section.content?.length || 0} chars
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSectionId(section.id);
                          // 🚨 FIXED: Add section to editing set
                          setEditingSections(prev => new Set([...prev, section.id]));
                          toggleTitleEditing(section.id);
                        }}
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      {onSectionAddAfter && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSectionAddAfter(section.id);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add After
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sections.length <= 1) {
                            // Could show a toast here
                            return;
                          }
                          if (confirm(`Are you sure you want to delete "${section.title}"?`)) {
                            handleDeleteSection(section.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
              
              {/* Add Section Button */}
              {onSectionAdd && (
                <div className="p-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={onSectionAdd}
                  >
                    <Plus className="h-4 w-4" />
                    Add Section
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Column 2: Editor */}
        <div className="flex-1 bg-white flex flex-col">
          <div className="flex-shrink-0 p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedSection && editingSections.has(selectedSection.id) ? "Rich Text Editor" : "Section Content"}
                </h3>
                {selectedSection && (
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-600">
                      Editing: {selectedSection.title}
                    </p>
                    {selectedSection.edited && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Modified
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedSection && !editingSections.has(selectedSection.id) && !readonly && (
                  <>
                    {/* Only show Regenerate Section button in regeneration flow, not in proposal generation */}
                    {onSectionRegenerate && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => {
                          // 🚨 FIXED: Call parent's regeneration handler directly, no internal dialog
                          console.log("🔄 YooptaContentEditor: Calling parent regeneration handler directly");
                          onSectionRegenerate(selectedSection.id, selectedSection.title);
                        }}
                        disabled={!onSectionRegenerate}
                      >
                        <Wand2 className="h-4 w-4" />
                        Regenerate Section
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Switching to edit mode:', {
                          selectedSectionId,
                          selectedSectionTitle: selectedSection?.title,
                          selectedSectionContent: selectedSection?.content?.length || 0,
                        });
                        // 🚨 FIXED: Add selected section to editing set
                        setEditingSections(prev => new Set([...prev, selectedSection.id]));
                        // Force editor re-initialization
                        setEditorKey(prev => prev + 1);
                      }}
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Section
                    </Button>
                  </>
                )}
                {selectedSection && editingSections.has(selectedSection.id) && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Switching to preview mode:', {
                          selectedSectionId,
                          selectedSectionTitle: selectedSection?.title,
                          selectedSectionContent: selectedSection?.content?.length || 0,
                          selectedSectionEdited: selectedSection?.edited,
                          selectedSectionLastModified: selectedSection?.lastModified
                        });
                        // 🚨 FIXED: Remove selected section from editing set
                        setEditingSections(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(selectedSection.id);
                          return newSet;
                        });
                        // Force preview update
                        setPreviewUpdateTrigger(prev => prev + 1);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                  
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            {selectedSection ? (
              <div className="flex-1">
                {editingSections.has(selectedSection.id) ? (
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1">
                      <YooptaMarkdownEditor
                        key={`editor-${selectedSection.id}-${editingSections.has(selectedSection.id) ? 'edit' : 'preview'}`}
                        initialValue={selectedSection.yooptaValue}
                        markdownContent={selectedSection.content}
                        onChange={(markdown) => {
                          console.log('🚨 YooptaMarkdownEditor onChange RECEIVED in YooptaContentEditor:', {
                            sectionId: selectedSection.id,
                            contentLength: markdown?.length || 0,
                            hasContent: !!(markdown && markdown.trim()),
                            preview: markdown ? markdown.substring(0, 50) + '...' : 'NO CONTENT'
                          });
                          handleSectionContentChange(markdown);
                        }}
                        placeholder={`Edit ${selectedSection.title} content...`}
                        className="h-full w-full"
                        autoFocus={true}
                        userId={userId}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    key={`preview-${selectedSection.id}-${previewUpdateTrigger}`}
                    className="flex-1 p-6"
                  >
                    <div className="prose prose-sm max-w-none">
                      {/* 🚨 FIXED: Only show title if content doesn't already contain it as H2 */}
                      {(() => {
                        const content = editedContent[selectedSection.id] || selectedSection.content || '';
                        const hasH2Title = content.includes(`## ${selectedSection.title}`);

                        return !hasH2Title && (
                          <h2 className="text-xl font-semibold mb-4 text-gray-900">
                            {selectedSection.title}
                          </h2>
                        );
                      })()}
                      <div className="mb-2 text-xs text-gray-500">
                        {((selectedSection as any).edited || editedContent[selectedSection.id]) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                            ✓ Modified
                          </span>
                        )}
                        Content length: {(editedContent[selectedSection.id] || selectedSection.content)?.length || 0} characters
                        {(selectedSection as any).lastModified && (
                          <span className="ml-2 text-gray-400">
                            Last modified: {new Date((selectedSection as any).lastModified).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                      <PerfectMarkdownRenderer
                        content={editedContent[selectedSection.id] || selectedSection.content || 'No content available'}
                        className="prose-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a section to start editing</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 🚨 REMOVED: Internal regeneration dialog - parent component handles this now */}
    </div>
  );
};

export default YooptaContentEditor;
export type { YooptaContentEditorProps };